import { useCallback, useMemo } from "react";
import { useAgentEngine } from "./useAgentEngine";
import { useAiModels } from "./useAiModels";

export interface ModelSelectorItem {
  readonly id: string;
  readonly label: string;
  readonly section: "agent" | "model";
  readonly engineName: string;
  readonly modelId?: string;
}

export interface UseModelSelectorResult {
  readonly items: readonly ModelSelectorItem[];
  readonly current: ModelSelectorItem | null;
  readonly loading: boolean;
  readonly select: (item: ModelSelectorItem) => Promise<void>;
}

const ENGINE_LABELS: Record<string, string> = {
  claude: "Claude Code",
  codex: "Codex",
};

export function useModelSelector(): UseModelSelectorResult {
  const { engines, current: currentEngine, loading: enginesLoading, setEngine } = useAgentEngine();
  const { models, defaults, loading: modelsLoading, setDefault } = useAiModels();

  const chatModels = useMemo(
    () => models.filter((m) => m.capability === "chat" && m.enabled),
    [models],
  );

  const chatDefault = useMemo(
    () => defaults.find((d) => d.capability === "chat"),
    [defaults],
  );

  const items = useMemo<ModelSelectorItem[]>(() => {
    const agentItems: ModelSelectorItem[] = engines
      .filter((e) => e !== "builtin")
      .map((name) => ({
        id: `engine:${name}`,
        label: ENGINE_LABELS[name] ?? name,
        section: "agent" as const,
        engineName: name,
      }));

    const modelItems: ModelSelectorItem[] = chatModels.map((m) => ({
      id: m.id,
      label: m.name,
      section: "model" as const,
      engineName: "builtin",
      modelId: m.model_id,
    }));

    return [...agentItems, ...modelItems];
  }, [engines, chatModels]);

  const current = useMemo<ModelSelectorItem | null>(() => {
    if (!currentEngine) return null;

    // If current engine is not builtin, find the agent item
    if (currentEngine !== "builtin") {
      return items.find((i) => i.section === "agent" && i.engineName === currentEngine) ?? null;
    }

    // Engine is builtin — find the active model
    if (chatDefault) {
      const found = items.find((i) => i.section === "model" && i.id === chatDefault.model_id);
      if (found) return found;
    }

    // Fallback to first model
    return items.find((i) => i.section === "model") ?? null;
  }, [currentEngine, chatDefault, items]);

  const select = useCallback(
    async (item: ModelSelectorItem): Promise<void> => {
      if (item.section === "agent") {
        await setEngine(item.engineName);
      } else {
        await setEngine("builtin");
        await setDefault("chat", item.id);
      }
    },
    [setEngine, setDefault],
  );

  return {
    items,
    current,
    loading: enginesLoading || modelsLoading,
    select,
  };
}
